import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { Button } from 'reactstrap';
import { connect } from 'react-redux';
import { toastr } from 'react-redux-toastr';
import Modal from '../../../../common/modal/Modal';
import {
  DEFAULT_QUERY,
  DEFAULT_QUERY_GET_ALL,
} from '../../../../../config/queries';
import {
  clearTickets,
  getTicketsForMassivePostpone,
  postImmediateTicketPostpone,
  postMassiveTicketActionModal,
} from '../../../../../actions';
import Table from '../../../../common/Table';
import DateCell from '../../../../common/cells/DateCell';
import { DATE_TIME_FORMAT } from '../../../../../config/locale';
import Loader from '../../../../common/Loader';
import ConfirmationModal from '../../../../common/modal/ConfirmationModal';
import withEndpointAuthorization from '../../../authorization/withEndPointAuthorization';
import { POST } from '../../../../../config/permissions';
import { TICKET_FOR_MASSIVE_POSTPONE_ENDPOINT } from '../../../../../config/endpoints';

export const ItineraryMassivePostponeButton = ({
  itineraryId,
  dispatchClearTickets,
  dispatchGetTicketsForMassivePostpone,
  dispatchPostMassiveTicketActionModal,
  dispatchPostImmediateTicketPostpone,
  ticketList,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [isImmediate] = useState(true);

  const [comment] = useState('Postergación masiva desde itinerario');

  const [isFetching, setIsFetching] = useState(false);
  const prevTicketListRef = useRef(ticketList);
  const hasSearchedRef = useRef(false);

  const [isPosting, setIsPosting] = useState(false);

  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState('');

  const [endpoint, setEndpoint] = useState(null);

  const payload = useMemo(
    () => ({
      itineraryId,
      ...DEFAULT_QUERY,
      size: DEFAULT_QUERY_GET_ALL.size,
      isImmediate,
    }),
    [itineraryId, isImmediate],
  );

  const runSearch = useCallback(() => {
    if (!itineraryId) {
      toastr.warning('Debe seleccionar un itinerario');
      return;
    }

    setIsFetching(true);
    hasSearchedRef.current = true;

    dispatchClearTickets();
    dispatchGetTicketsForMassivePostpone(payload);
  }, [
    dispatchClearTickets,
    dispatchGetTicketsForMassivePostpone,
    itineraryId,
    payload,
  ]);

  const openModal = () => {
    if (!itineraryId) {
      toastr.warning('Debe seleccionar un itinerario');
      return;
    }
    setShowModal(true);
    runSearch();
  };

  const closeModal = () => {
    dispatchClearTickets();

    setShowModal(false);
  };

  useEffect(() => {
    if (!showModal) {
      setIsFetching(false);
      hasSearchedRef.current = false;
    }
  }, [showModal]);

  useEffect(() => {
    if (!showModal) return;
    if (!hasSearchedRef.current) return;

    const prev = prevTicketListRef.current;
    const curr = ticketList;

    if (prev !== curr) {
      setIsFetching(false);
      prevTicketListRef.current = curr;
    }
  }, [ticketList, showModal]);

  const safeTicketArray = useMemo(() => {
    if (ticketList && typeof ticketList.toJS === 'function')
      return ticketList.toJS();
    if (Array.isArray(ticketList)) return ticketList;
    return [];
  }, [ticketList]);

  const ticketsCount = useMemo(() => {
    if (!ticketList) return 0;
    if (typeof ticketList.size === 'number') return ticketList.size;
    if (Array.isArray(ticketList)) return ticketList.length;
    return safeTicketArray.length;
  }, [ticketList, safeTicketArray]);

  const handleMassivePostpone = useCallback(async () => {
    if (!itineraryId) {
      toastr.warning('Debe seleccionar un itinerario');
      return;
    }

    if (!safeTicketArray || safeTicketArray.length === 0) {
      toastr.warning('No hay boletos para postergar');
      return;
    }

    const formValues = {
      comment,
      isImmediate,
      ticketIdList: safeTicketArray.map((t) => t.id).filter(Boolean),
    };

    setIsPosting(true);
    try {
      const response = await dispatchPostMassiveTicketActionModal(formValues);

      if (response && response.message) {
        if (response.url) setEndpoint(response.url);
        setConfirmationMessage(response.message);
        setShowConfirmationModal(true);
      } else {
        toastr.error('No se recibió respuesta válida del servidor');
      }
    } catch (e) {
      toastr.error('Error al procesar la postergación masiva');
    } finally {
      setIsPosting(false);
    }
  }, [
    dispatchPostMassiveTicketActionModal,
    itineraryId,
    isImmediate,
    safeTicketArray,
  ]);

  const shouldShowLoader = isFetching;

  const modalBody = (
    <div>
      {shouldShowLoader ? (
        <Loader />
      ) : (
        <>
          <h2>Se Postergará los siguientes boletos</h2>

          <p style={{ marginBottom: 8 }}>
            <strong>Boletos encontrados:</strong> {ticketsCount}
          </p>

          {safeTicketArray.length === 0 ? (
            <div style={{ padding: '10px 0' }}>
              No se encontraron boletos para este itinerario.
            </div>
          ) : (
            <Table
              columns={[
                {
                  Header: 'Serie',
                  accessor: 'documentSeries',
                  className: 'text-center',
                  width: 100,
                },
                {
                  Header: 'Correlativo',
                  accessor: 'documentCode',
                  className: 'text-center',
                  width: 100,
                },
                {
                  Header: 'Origen',
                  accessor: 'sourceLocation.name',
                  className: 'text-center',
                },
                {
                  Header: 'Fecha de Viaje',
                  accessor: 'departureDate',
                  className: 'text-center',
                  Cell: (row) => (
                    <DateCell value={row.value} format={DATE_TIME_FORMAT} />
                  ),
                },
                {
                  Header: 'Destino',
                  accessor: 'destinationLocation.name',
                  className: 'text-center',
                },
                {
                  Header: 'Cliente',
                  accessor: 'customer.fullName',
                  className: 'text-center',
                },
              ]}
              data={safeTicketArray}
              manual={false}
            />
          )}
        </>
      )}

      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 8,
          marginTop: 12,
        }}
      >
        <Button color="secondary" onClick={closeModal} disabled={isPosting}>
          Cerrar
        </Button>

        <Button
          color="primary"
          onClick={handleMassivePostpone}
          disabled={
            shouldShowLoader || isPosting || safeTicketArray.length === 0
          }
          title={safeTicketArray.length === 0 ? 'No hay boletos' : ''}
        >
          {isPosting ? 'Procesando...' : 'Continuar'}
        </Button>
      </div>
    </div>
  );

  const confirmationBody = (
    <div>
      <p>{confirmationMessage}</p>
    </div>
  );

  const handlePostponeTicket = async () => {
    if (!endpoint) {
      toastr.error('No se encontró el endpoint para confirmar la postergación');
      return;
    }

    setIsPosting(true);
    try {
      if (isImmediate) {
        await dispatchPostImmediateTicketPostpone({
          endpoint,
          comment,
          ticketIdList: safeTicketArray.map((t) => t.id).filter(Boolean),
          itineraryId,
          redirect: false,
        });
      }

      setShowConfirmationModal(false);
      setShowModal(false);
    } catch (e) {
      toastr.error('Error al confirmar la postergación');
    } finally {
      setIsPosting(false);
    }
  };

  return (
    <>
      <Modal
        show={showModal}
        title="Postergación Masiva"
        body={modalBody}
        size="xl"
        onClickCancel={closeModal}
        onClickClose={closeModal}
      />

      <ConfirmationModal
        show={showConfirmationModal}
        title="Confirmación"
        body={confirmationBody}
        onClickConfirm={handlePostponeTicket}
        size="md"
        onClickCancel={() => setShowConfirmationModal(false)}
        onClickClose={() => setShowConfirmationModal(false)}
      />

      <Button
        color="warning"
        className="border"
        onClick={openModal}
        disabled={!itineraryId}
        title={!itineraryId ? 'Seleccione un itinerario' : ''}
      >
        <i className="fa fa-clock-o" /> Postergación masiva
      </Button>
    </>
  );
};

const mapStateToProps = ({ ReservationUnit }) => ({
  ticketList: ReservationUnit.Ticket.getIn(['all', 'content', 'content']),
});

ItineraryMassivePostponeButton.propTypes = {
  itineraryId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  dispatchClearTickets: PropTypes.func.isRequired,
  dispatchGetTicketsForMassivePostpone: PropTypes.func.isRequired,
  dispatchPostMassiveTicketActionModal: PropTypes.func.isRequired,
  dispatchPostImmediateTicketPostpone: PropTypes.func.isRequired,
  ticketList: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
};

ItineraryMassivePostponeButton.defaultProps = {
  ticketList: null,
};

const mapDispatchToProps = {
  dispatchGetTicketsForMassivePostpone: getTicketsForMassivePostpone,
  dispatchPostMassiveTicketActionModal: postMassiveTicketActionModal,
  dispatchPostImmediateTicketPostpone: postImmediateTicketPostpone,
  dispatchClearTickets: clearTickets,
};

const connectedComponent = connect(
  mapStateToProps,
  mapDispatchToProps,
)(ItineraryMassivePostponeButton);

export default withEndpointAuthorization({
  url: TICKET_FOR_MASSIVE_POSTPONE_ENDPOINT,
  permissionType: POST,
})(connectedComponent);
